import { ShieldCheck, ShieldOff } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import useSWR from "swr";
import { apiFetch, jsonBody, swrFetcher } from "@/web/api";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/web/components/ui/card";
import { Input } from "@/web/components/ui/input";
import { Label } from "@/web/components/ui/label";

interface TotpSetup {
  secret: string;
  uri: string;
}

export function TwoFactorCard() {
  const { data, mutate } = useSWR<{ enabled: boolean }>("/admin-api/2fa/status", swrFetcher);
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    if (!setup) {
      setQr("");
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(setup.uri, { width: 320, margin: 1, errorCorrectionLevel: "M", color: { dark: "#09090b", light: "#ffffff" } })
      .then((url) => { if (!cancelled) setQr(url); })
      .catch(() => { if (!cancelled) setQr(""); });
    return () => { cancelled = true; };
  }, [setup]);

  async function startSetup() {
    setBusy(true);
    try {
      const result = await apiFetch<TotpSetup>("/admin-api/2fa/setup", { method: "POST" });
      setSetup(result);
      setCode("");
      setDisabling(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "生成密钥失败");
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnable() {
    setBusy(true);
    try {
      await apiFetch("/admin-api/2fa/enable", { method: "POST", ...jsonBody({ code }) });
      toast.success("双重验证已启用");
      setSetup(null);
      setCode("");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "启用失败");
    } finally {
      setBusy(false);
    }
  }

  async function confirmDisable() {
    setBusy(true);
    try {
      await apiFetch("/admin-api/2fa/disable", { method: "POST", ...jsonBody({ code }) });
      toast.success("双重验证已关闭");
      setDisabling(false);
      setCode("");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "关闭失败");
    } finally {
      setBusy(false);
    }
  }

  const codeField = (
    <Input
      inputMode="numeric"
      maxLength={6}
      placeholder="6 位数字"
      value={code}
      onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
      className="w-40 font-mono tracking-[0.3em]"
    />
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>双重验证</CardTitle>
        <CardDescription>启用后，登录除密码外还需输入身份验证器生成的 6 位动态码。</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data?.enabled ? (
          disabling ? (
            <div className="space-y-3">
              <p className="text-sm text-muted">输入当前动态码以确认关闭。</p>
              <div className="flex items-center gap-2">{codeField}<Button variant="danger" disabled={busy || code.length !== 6} onClick={confirmDisable}>确认关闭</Button><Button variant="ghost" disabled={busy} onClick={() => { setDisabling(false); setCode(""); }}>取消</Button></div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-4 rounded-md border p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 size-5 shrink-0 text-success" />
                <div>
                  <div className="text-sm font-medium">已启用</div>
                  <p className="mt-1 text-xs leading-5 text-muted">登录时需要密码 + 动态码。</p>
                </div>
              </div>
              <Button variant="outline" onClick={() => { setDisabling(true); setSetup(null); setCode(""); }}><ShieldOff />关闭</Button>
            </div>
          )
        ) : setup ? (
          <div className="space-y-4">
            <p className="text-sm text-muted">用身份验证器 App（如 Google Authenticator、Microsoft Authenticator）扫描下方二维码。</p>
            <div className="flex flex-wrap items-start gap-5">
              {qr ? <img src={qr} alt="双重验证二维码" className="size-40 rounded-lg border bg-white p-2" /> : null}
              <div className="min-w-0 space-y-2">
                <Label>无法扫码？手动输入密钥</Label>
                <code className="block break-all rounded-md bg-foreground/[0.04] px-3 py-2 font-mono text-xs">{setup.secret}</code>
              </div>
            </div>
            <div className="space-y-3 border-t pt-4">
              <Label htmlFor="totp-confirm">输入 App 上显示的 6 位动态码以完成启用</Label>
              <div className="flex items-center gap-2">{codeField}<Button disabled={busy || code.length !== 6} onClick={confirmEnable}>确认启用</Button><Button variant="ghost" disabled={busy} onClick={() => { setSetup(null); setCode(""); }}>取消</Button></div>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-4 rounded-md border p-4">
            <div className="flex items-start gap-3">
              <ShieldOff className="mt-0.5 size-5 shrink-0 text-muted" />
              <div>
                <div className="text-sm font-medium">未启用</div>
                <p className="mt-1 text-xs leading-5 text-muted">强烈建议启用，避免密码泄露后后台被直接登录。</p>
              </div>
            </div>
            <Button disabled={busy} onClick={startSetup}><ShieldCheck />启用</Button>
          </div>
        )}

        <p className="border-t pt-4 text-xs leading-5 text-muted">
          验证器丢失或无法登录时，可在服务器执行项目目录下的
          <code className="mx-1 rounded bg-foreground/[0.06] px-1.5 py-0.5 font-mono text-[11px]">bash reset-2fa.sh</code>
          紧急关闭双重验证（无需停机）。建议同时保存好启用时显示的密钥。
        </p>
      </CardContent>
    </Card>
  );
}
